// =========================
// Support — BL-037 Story 37.1 (Customer Feedback & Support Capture)
//
// A lightweight, low-friction in-app channel for a controlled-onboarding user
// to submit Feedback, a Support request, a Bug report, or a Feature request —
// replacing the untracked landing-page mailto:. Submissions persist to the
// user-owned support_requests table (via lib/support → /submit-support-request)
// so they're observable in the Admin Portal.
//
// Design-system tokens only (no hex — CLAUDE.md). NO payment/card capture: the
// form explicitly warns against entering billing details (PCI surface = zero).
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { submitSupportRequest, listMySupportRequests } from '../lib/support';

// Customer-facing labels → stored category keys (the API allowlist).
const CATEGORIES = [
  { key: 'feedback', label: 'Feedback' },
  { key: 'support', label: 'Support' },
  { key: 'bug', label: 'Bug Report' },
  { key: 'feature_request', label: 'Feature Request' },
];

function categoryLabel(key) {
  return CATEGORIES.find((c) => c.key === key)?.label || key;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

export default function Support() {
  const [category, setCategory] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [mine, setMine] = useState([]);

  const loadMine = useCallback(async () => {
    const { data } = await listMySupportRequests();
    if (Array.isArray(data)) setMine(data);
  }, []);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);

    // Client-side guard — never call the API with an empty subject/message.
    if (!subject.trim() || !message.trim()) {
      setError('Please add a subject and a message.');
      return;
    }

    setSubmitting(true);
    const { error: submitError } = await submitSupportRequest({
      category,
      subject: subject.trim(),
      message: message.trim(),
    });
    setSubmitting(false);

    if (submitError) {
      setError(submitError.message || 'Could not submit your request.');
      return;
    }

    setSubmitted(true);
    setSubject('');
    setMessage('');
    loadMine();
  };

  const labelStyle = {
    display: 'block',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-semibold)',
    color: 'var(--color-steel-lt)',
    marginBottom: 'var(--space-1)',
  };

  const fieldStyle = {
    width: '100%',
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-steel-dk)',
    background: 'var(--color-navy)',
    color: 'var(--color-white)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-base)',
  };

  return (
    <div
      style={{
        background: 'var(--color-navy)',
        minHeight: '100%',
        padding: 'var(--space-6)',
        color: 'var(--color-white)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto', display: 'grid', gap: 'var(--space-5)' }}>
        <header>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              textTransform: 'uppercase',
              margin: 0,
              marginBottom: 'var(--space-2)',
            }}
          >
            Help &amp; Feedback
          </h1>
          <p style={{ fontSize: 'var(--text-md)', color: 'var(--color-steel-lt)', margin: 0 }}>
            Tell us what&apos;s working, what isn&apos;t, or what you&apos;d like to see. We read
            every submission.
          </p>
        </header>

        {submitted && (
          <div
            role="status"
            className="card"
            style={{
              padding: 'var(--space-4) var(--space-5)',
              border: '1px solid var(--color-success)',
              background: 'var(--color-success-ghost)',
              color: 'var(--color-success-lt)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            Thank you — your request was submitted. We&apos;ll follow up by email if needed.
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="card"
            style={{
              padding: 'var(--space-4) var(--space-5)',
              border: '1px solid var(--color-danger)',
              background: 'var(--color-danger-ghost)',
              color: 'var(--color-danger-lt)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="card" style={{ padding: 'var(--space-5)', display: 'grid', gap: 'var(--space-4)' }}>
          <div>
            <label htmlFor="support-category" style={labelStyle}>What can we help with?</label>
            <select
              id="support-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={fieldStyle}
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="support-subject" style={labelStyle}>Subject</label>
            <input
              id="support-subject"
              type="text"
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A short summary"
              style={fieldStyle}
            />
          </div>

          <div>
            <label htmlFor="support-message" style={labelStyle}>Message</label>
            <textarea
              id="support-message"
              value={message}
              maxLength={5000}
              rows={6}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what happened or what you need"
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          </div>

          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-muted)', margin: 0 }}>
            Please do not include payment or card details in your message. For billing help, use
            the Account page.
          </p>

          <div>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>

        {mine.length > 0 && (
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-bold)',
                margin: 0,
              }}
            >
              Your requests
            </h2>
            {mine.map((row) => (
              <div
                key={row.id}
                className="card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-2) var(--space-4)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  <span style={{ fontWeight: 'var(--weight-bold)' }}>{row.subject}</span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
                    {categoryLabel(row.category)} · {formatDate(row.created_at)}
                  </span>
                </div>
                <span className="badge">{row.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
